/**
 * Admin Page Shell
 * Wrapper component that provides consistent layout, error boundary, and states
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import AdminErrorBoundary from './AdminErrorBoundary';
import { AdminLoadingState, AdminErrorState } from './AdminStates';

/**
 * Admin Page Shell - Wraps admin pages with consistent layout and error handling
 */
export const AdminPageShell = ({
  children,
  title,
  subtitle,
  icon: Icon,
  headerColor = 'from-purple-600 to-indigo-600',
  backLink = '/admin',
  backLabel = 'Back to Admin Dashboard',
  loading = false,
  error = null,
  isAuthError = false,
  onRetry,
  headerActions,
  className = '',
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backLink) {
      navigate(backLink);
    } else {
      navigate(-1);
    }
  };

  return (
    <AdminErrorBoundary>
      <div className={`min-h-screen bg-black text-white ${className}`} data-testid="admin-page-shell">
        {/* Header */}
        <div className={`bg-gradient-to-r ${headerColor} py-6 px-4 sm:px-6 lg:px-8`}>
          <div className="max-w-7xl mx-auto">
            {backLink && (
              <button 
                onClick={handleBack}
                className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {backLabel}
              </button>
            )}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                  {Icon && <Icon className="w-7 h-7 sm:w-8 sm:h-8" />}
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-white/80 mt-2">{subtitle}</p>
                )}
              </div>
              {headerActions && (
                <div className="flex items-center gap-2">
                  {headerActions}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {loading ? (
            <AdminLoadingState message="Loading data..." size="large" />
          ) : error ? (
            <AdminErrorState 
              error={error} 
              isAuthError={isAuthError} 
              onRetry={onRetry}
            />
          ) : (
            children
          )}
        </div>
      </div>
    </AdminErrorBoundary>
  );
};

/**
 * Admin Card Shell - Consistent card wrapper for admin content
 */
export const AdminCardShell = ({
  children,
  title,
  subtitle,
  icon: Icon,
  actions,
  loading = false,
  error = null,
  onRetry,
  className = '',
}) => {
  return (
    <div className={`bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden ${className}`}>
      {(title || actions) && (
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {Icon && <Icon className="w-5 h-5 text-purple-400" />}
            <div>
              {title && <h2 className="font-semibold text-white">{title}</h2>}
              {subtitle && <p className="text-sm text-zinc-400">{subtitle}</p>}
            </div>
          </div>
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      <div className="p-6">
        {loading ? (
          <AdminLoadingState size="small" />
        ) : error ? (
          <AdminErrorState error={error} onRetry={onRetry} />
        ) : (
          children
        )}
      </div>
    </div>
  );
};

/**
 * Admin Section - Consistent section wrapper
 */
export const AdminSection = ({
  children,
  title,
  description,
  actions,
  className = '',
}) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between">
          <div>
            {title && <h2 className="text-xl font-bold text-white">{title}</h2>}
            {description && <p className="text-zinc-400 mt-1">{description}</p>}
          </div>
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

export default AdminPageShell;
